import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import CategoryNavigation from './components/CategoryNavigation';
import ContentLibrary from './components/ContentLibrary';
import SearchFilters from './components/SearchFilters';
import ContentUploader from './components/ContentUploader';
import VersionControl from './components/VersionControl';
import ContentAnalytics from './components/ContentAnalytics';

const TrainingMaterialsRepository = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showUploader, setShowUploader] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    type: 'all',
    level: 'all',
    format: 'all',
    status: 'all'
  });

  const categories = [
    { id: 'all', name: 'All Materials', count: 245, icon: 'FolderOpen' },
    { id: 'mandatory', name: 'Mandatory Safety', count: 89, icon: 'Shield' },
    { id: 'skill-based', name: 'Skill-Based Courses', count: 67, icon: 'Award' },
    { id: 'regulatory', name: 'Regulatory Compliance', count: 45, icon: 'FileCheck' },
    { id: 'equipment', name: 'Equipment Training', count: 34, icon: 'Wrench' },
    { id: 'assessments', name: 'Assessments & Quizzes', count: 10, icon: 'ClipboardCheck' }
  ];

  const contentLibrary = [
    {
      id: 1,
      title: 'Safety Orientation - Complete Module',
      description: 'Comprehensive safety orientation for new employees covering workplace safety basics, emergency procedures, and company policies.',
      category: 'mandatory',
      type: 'course',
      format: 'video',
      duration: '45 min',
      level: 'beginner',
      version: '2.1',
      lastUpdated: '11/15/2025',
      status: 'published',
      completions: 456,
      rating: 4.8,
      thumbnail: '/assets/images/no_image.png',
      hasAssessment: true,
      requiredFor: ['All Employees']
    },
    {
      id: 2,
      title: 'Fire Safety & Emergency Response',
      description: 'Fire prevention, extinguisher operation, evacuation procedures, and emergency response protocols.',
      category: 'mandatory',
      type: 'course',
      format: 'interactive',
      duration: '30 min',
      level: 'beginner',
      version: '1.5',
      lastUpdated: '11/20/2025',
      status: 'published',
      completions: 442,
      rating: 4.9,
      thumbnail: '/assets/images/no_image.png',
      hasAssessment: true,
      requiredFor: ['All Employees']
    },
    {
      id: 3,
      title: 'Hazardous Materials Handling',
      description: 'Safe handling, storage, and disposal of hazardous materials. Includes SDS review and spill response procedures.',
      category: 'regulatory',
      type: 'course',
      format: 'document',
      duration: '60 min',
      level: 'intermediate',
      version: '3.0',
      lastUpdated: '12/01/2025',
      status: 'published',
      completions: 398,
      rating: 4.7,
      thumbnail: '/assets/images/no_image.png',
      hasAssessment: true,
      requiredFor: ['Production', 'Maintenance', 'Warehouse']
    },
    {
      id: 4,
      title: 'Forklift Operation Certification',
      description: 'Complete forklift operator training including pre-operation inspection, safe operation techniques, and load handling.',
      category: 'equipment',
      type: 'certification',
      format: 'video',
      duration: '120 min',
      level: 'intermediate',
      version: '2.3',
      lastUpdated: '11/10/2025',
      status: 'published',
      completions: 89,
      rating: 4.9,
      thumbnail: '/assets/images/no_image.png',
      hasAssessment: true,
      requiredFor: ['Warehouse', 'Production']
    },
    {
      id: 5,
      title: 'Confined Space Entry Procedures',
      description: 'Entry procedures, atmospheric testing, emergency rescue, and confined space hazard recognition.',
      category: 'skill-based',
      type: 'course',
      format: 'interactive',
      duration: '90 min',
      level: 'advanced',
      version: '1.8',
      lastUpdated: '11/25/2025',
      status: 'published',
      completions: 67,
      rating: 4.8,
      thumbnail: '/assets/images/no_image.png',
      hasAssessment: true,
      requiredFor: ['Maintenance']
    },
    {
      id: 6,
      title: 'Lock-Out Tag-Out (LOTO) Training',
      description: 'Energy isolation procedures for equipment maintenance and repair. Includes practical exercises and verification steps.',
      category: 'mandatory',
      type: 'course',
      format: 'video',
      duration: '45 min',
      level: 'intermediate',
      version: '2.0',
      lastUpdated: '12/05/2025',
      status: 'published',
      completions: 234,
      rating: 4.7,
      thumbnail: '/assets/images/no_image.png',
      hasAssessment: true,
      requiredFor: ['Maintenance', 'Production']
    }
  ];

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleContentSelect = (content) => {
    setSelectedContent(content);
  };

  const handleBackToDashboard = () => {
    navigate('/training-compliance-dashboard');
  };

  const handleUploadComplete = (newContent) => {
    console.log('New content uploaded:', newContent);
    setShowUploader(false);
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleBackToDashboard}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="ArrowLeft" size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-foreground mb-2">Training Materials Repository</h1>
            <p className="text-muted-foreground">
              Comprehensive management of training content, courses, and learning resources
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowUploader(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Icon name="Upload" size={20} />
              <span className="font-medium">Upload Content</span>
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-colors">
              <Icon name="Download" size={20} />
              <span className="font-medium">Bulk Export</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar - Category Navigation */}
        <div className="lg:col-span-1">
          <CategoryNavigation
            categories={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategorySelect}
          />
          
          <div className="mt-6">
            <ContentAnalytics />
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Search and Filters */}
          <SearchFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filters={selectedFilters}
            onFiltersChange={setSelectedFilters}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          {/* Content Library */}
          <ContentLibrary
            content={contentLibrary}
            selectedCategory={selectedCategory}
            viewMode={viewMode}
            searchQuery={searchQuery}
            filters={selectedFilters}
            onContentSelect={handleContentSelect}
          />

          {/* Version Control Panel */}
          {selectedContent && (
            <VersionControl
              content={selectedContent}
              onClose={() => setSelectedContent(null)}
            />
          )}
        </div>
      </div>

      {/* Content Uploader Modal */}
      {showUploader && (
        <ContentUploader
          onClose={() => setShowUploader(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {/* Offline Access Info */}
      <div className="bg-card border border-border rounded-lg p-6 mt-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Download" size={24} color="var(--color-accent)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Offline Access Available</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Download training materials for offline access in field locations. Content automatically syncs when connection is restored.
            </p>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
              <Icon name="Download" size={16} />
              Enable Offline Mode
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingMaterialsRepository;